import { KIND_TO_FIELD } from '@/api/types/stowed-element'
import type { PageElement, StowedElement } from '@/api/types/stowed-element'

/** Rotate request contract: per-element stubs of objectId plus the five rotate-owned base fields. */
export interface RotateElementDto {
  objectId: string
  visibleX?: number
  visibleY?: number
  visibleWidth?: number
  visibleHeight?: number
  rotation?: number
}

/** Go decimals currently serialize as JSON strings in endpoint responses. */
export interface RotateElementDtoResponse {
  objectId: string
  visibleX: number | string
  visibleY: number | string
  visibleWidth: number | string
  visibleHeight: number | string
  rotation: number | string
}

function activeBase(element: StowedElement): PageElement | undefined {
  const wire = element as unknown as Record<string, unknown>
  const field = KIND_TO_FIELD[element.kindId]
  const body = field ? wire[field] : undefined
  const base = body && typeof body === 'object' ? (body as { base?: unknown }).base : undefined

  if (base && typeof base === 'object') return base as PageElement

  // Kind-8 rows serialized before ovals carried their own body keep the base
  // under rectangleElement; read it from there when the oval body is absent.
  if (element.kindId === 8) {
    const legacyOvalBody = wire.rectangleElement
    const legacyOvalBase =
      legacyOvalBody && typeof legacyOvalBody === 'object'
        ? (legacyOvalBody as { base?: unknown }).base
        : undefined

    if (legacyOvalBase && typeof legacyOvalBase === 'object') return legacyOvalBase as PageElement
  }

  return undefined
}

/**
 * The rotate endpoint rejects payloads missing any rotate-owned field rather than defaulting
 * them, so an element without a resolvable base is sent without those fields and fails
 * validation server-side instead of silently rotating stale geometry.
 */
export function rotateElementDtoFromStowedElement(element: StowedElement): RotateElementDto {
  const base = activeBase(element)

  return {
    objectId: element.objectId,
    visibleX: base?.visibleX,
    visibleY: base?.visibleY,
    visibleWidth: base?.visibleWidth,
    visibleHeight: base?.visibleHeight,
    rotation: base?.rotation,
  }
}

export function rotateElementDtosFromStowedElements(elements: StowedElement[]): RotateElementDto[] {
  return elements.map(rotateElementDtoFromStowedElement)
}
